import { Box } from '@mui/material';
import { useStyles } from './InteractiveTreatmentPlan.styles';
import { Icons, Input, inputType, inputSize, Button, btnType, btnSize, Checkbox } from '../../../../../index';
import { useState } from 'react';

/**
 * Step 3 of Interactive Treatment Plan Flow
 * Displays selected goals with their objectives for review and editing
 */
const InteractiveObjectivesStep = ({
  selectedGoals = [],
  onToggleObjective,
  onEditObjective,
  onAddObjective,
  onViewReasoning,
}) => {
  const classes = useStyles();
  const [editingObjectiveId, setEditingObjectiveId] = useState(null);
  const [editedObjectiveText, setEditedObjectiveText] = useState('');
  const [addingObjectiveToGoal, setAddingObjectiveToGoal] = useState(null);
  const [newObjectiveText, setNewObjectiveText] = useState('');

  const handleStartEditObjective = (objective) => {
    setEditingObjectiveId(objective.id);
    setEditedObjectiveText(objective.details);
  };

  const handleSaveObjective = (goalId, objectiveId) => {
    if (editedObjectiveText.trim()) {
      onEditObjective(goalId, objectiveId, editedObjectiveText.trim());
      setEditingObjectiveId(null);
      setEditedObjectiveText('');
    }
  };

  const handleCancelEdit = () => {
    setEditingObjectiveId(null);
    setEditedObjectiveText('');
  };

  const handleStartAddObjective = (goalId) => {
    setAddingObjectiveToGoal(goalId);
    setNewObjectiveText('');
  };

  const handleSaveNewObjective = (goalId) => {
    if (newObjectiveText.trim()) {
      onAddObjective(goalId, newObjectiveText.trim());
      setAddingObjectiveToGoal(null);
      setNewObjectiveText('');
    }
  };

  const handleCancelAddObjective = () => {
    setAddingObjectiveToGoal(null);
    setNewObjectiveText('');
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      {selectedGoals.map((goal, goalIndex) => (
        <Box key={goal.id || goalIndex} className={classes.objectiveGoalCard}>
          <Box className={classes.objectiveGoalHeader}>
            <Box display="flex" flexDirection="column" gap="4px" flex={1}>
              <Box className={classes.goalName}>{goal.name}</Box>
              {goal.expectedCompletion && (
                <Box className={classes.expectedCompletion}>
                  Expected completion: {goal.expectedCompletion}
                </Box>
              )}
            </Box>
          </Box>

           <Box className={classes.objectivesLabel}>Objectives:</Box>
           <Box display="flex" flexDirection="column" gap="12px">
             {goal.objectives?.map(objective => {
               objective.id = objective._id || objective.id;
               return objective;
             }).map((objective, objIndex) => (
               <Box
                 key={objective.id || objIndex}
                 className={ objective.preSelected !== false ? classes.objectiveItemSelected : classes.objectiveItem}
               >
                 {editingObjectiveId === objective.id ? (
                   // Editing mode - NO checkbox, just input + save button
                   <Box display="flex" gap="20px" flex={1} alignItems="center">
                     <Input
                       fullWidth
                       multiline
                       rows={3}
                       value={editedObjectiveText}
                       onChange={(e) => setEditedObjectiveText(e.target.value)}
                       variant={inputType.DEFAULT}
                       size={inputSize.M}
                       autoFocus
                     />
                     <Icons
                       glyph="save-outlined"
                       className={classes.iconButton}
                       onClick={() => handleSaveObjective(goal.id, objective.id)}
                     />
                   </Box>
                 ) : (
                   // Display mode - checkbox + text + edit icon (only for unselected)
                   <>
                     <Checkbox
                       checked={objective.preSelected !== false}
                       onChange={() =>
                         onToggleObjective(goal.id, objective.id || objIndex)
                       }
                       className={classes.objectiveCheckbox}
                     />
                     <Box className={classes.objectiveText}>
                       {objective.details}
                     </Box>
                     {/* Show edit icon only for unselected objectives */}
                     {objective.preSelected === false && (
                       <Icons
                         glyph="edit-underline"
                         className={classes.editIcon}
                         onClick={() => handleStartEditObjective(objective)}
                       />
                     )}
                   </>
                 )}
               </Box>
             ))}

             {/* Add new objective input row - NO checkbox, just input + save */}
             {addingObjectiveToGoal === goal.id && (
               <Box className={classes.objectiveItem}>
                 <Box display="flex" gap="20px" flex={1} alignItems="center">
                   <Input
                     fullWidth
                     multiline
                     rows={2}
                     value={newObjectiveText}
                     onChange={(e) => setNewObjectiveText(e.target.value)}
                     placeholder="Enter objective description..."
                     variant={inputType.DEFAULT}
                     size={inputSize.M}
                     autoFocus
                   />
                   <Icons
                     glyph="save-outlined"
                     className={classes.iconButton}
                     onClick={() => handleSaveNewObjective(goal.id)}
                   />
                 </Box>
               </Box>
             )}
          </Box>
          {/* Bottom actions */}
          <Box display="flex" justifyContent="space-between" alignItems="center" padding="8px">
            {goal.objectives?.some(obj => obj.rationale) ? (
              <Box
                className={classes.viewReasoningLink}
                onClick={() => onViewReasoning(goal.id)}
              >
                View the reasoning
              </Box>
            ) : <Box/>}
            <Box
              className={classes.viewReasoningLink}
              onClick={() => {
                // Add to the first goal by default, or you can make this smarter
                if (selectedGoals.length > 0) {
                  handleStartAddObjective(goal.id);
                }
              }}
            >
              Add Objective
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export { InteractiveObjectivesStep };

